/**
 * "Resend invitation", for someone who has not signed in yet.
 *
 * The temporary password in the first email expires after seven days, and until now an
 * invitation nobody got round to was a dead end: the forgotten-password flow does not
 * work for an account that has never set a password. This sends a fresh one.
 *
 * Says what happened in place — "Sent", or why not — because the result is an email
 * the administrator cannot see, and a button that silently succeeds invites a second
 * and third press.
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { useI18n } from '@/i18n';
import { api, ApiError } from '@/lib/api';

export function ReinviteButton({ userId, compact = false }: { userId: string; compact?: boolean }) {
  const { t } = useI18n();
  const [sentTo, setSentTo] = useState<string | null>(null);

  const reinvite = useMutation({
    mutationFn: () => api.reinviteUser(userId),
    onSuccess: (r) => setSentTo(r.email),
  });

  const error = reinvite.error
    ? reinvite.error instanceof ApiError
      ? reinvite.error.message
      : t('common.saveError')
    : null;

  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <button
        type="button"
        className={compact ? 'text-xs font-semibold text-primary hover:underline disabled:opacity-60' : 'btn-secondary'}
        disabled={reinvite.isPending || sentTo !== null}
        title={sentTo ? t('access.reinviteSent', { email: sentTo }) : t('access.reinviteHint')}
        onClick={() => reinvite.mutate()}
      >
        {reinvite.isPending
          ? t('access.reinviting')
          : sentTo
            ? t('access.reinviteDone')
            : t('access.reinvite')}
      </button>
      {sentTo && !compact ? (
        <span className="text-xs text-success" role="status">
          {t('access.reinviteSent', { email: sentTo })}
        </span>
      ) : null}
      {error ? (
        <span className="max-w-xs text-xs text-danger" role="alert">
          {error}
        </span>
      ) : null}
    </span>
  );
}
